import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Switch,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import { format } from 'date-fns'

import CreateFlowDialog from '../components/dialogs/CreateFlowDialog'
import { useNotification } from '../contexts/NotificationContext'

interface Flow {
  id: string
  name: string
  description: string
  trigger: 'new_lead' | 'qualified_lead' | 'booking_request' | 'manual'
  status: 'active' | 'inactive' | 'draft'
  createdAt: Date
  lastRun?: Date
  totalRuns: number
  successRate: number
}

const Flows: React.FC = () => {
  const { showNotification } = useNotification()
  const [createFlowOpen, setCreateFlowOpen] = useState(false)
  const [loading, _setLoading] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{ anchorEl: HTMLElement; flowId: string } | null>(null)

  // Mock data
  const flows: Flow[] = [
    {
      id: '1',
      name: 'Welcome & Qualification',
      description: 'Greets new leads and asks qualification questions',
      trigger: 'new_lead',
      status: 'active',
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      lastRun: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      totalRuns: 156,
      successRate: 78,
    },
    {
      id: '2',
      name: 'Booking Follow-up',
      description: 'Follows up with qualified leads to book appointments',
      trigger: 'qualified_lead',
      status: 'active',
      createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
      lastRun: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      totalRuns: 89,
      successRate: 65,
    },
    {
      id: '3',
      name: 'Appointment Reminder',
      description: 'Sends reminders 24h and 2h before appointments',
      trigger: 'booking_request',
      status: 'active',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
      lastRun: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
      totalRuns: 234,
      successRate: 92,
    },
    {
      id: '4',
      name: 'Re-engagement Campaign',
      description: 'Reaches out to inactive leads after 30 days',
      trigger: 'manual',
      status: 'inactive',
      createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000), // 21 days ago
      totalRuns: 45,
      successRate: 34,
    },
    {
      id: '5',
      name: 'Holiday Promotion',
      description: 'Seasonal promotion flow for special offers',
      trigger: 'manual',
      status: 'draft',
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      totalRuns: 0,
      successRate: 0,
    },
  ]

  const getTriggerLabel = (trigger: string) => {
    const labels = {
      new_lead: 'New Lead',
      qualified_lead: 'Qualified Lead',
      booking_request: 'Booking Request',
      manual: 'Manual',
    }
    return labels[trigger as keyof typeof labels] || trigger
  }

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'success',
      inactive: 'error',
      draft: 'warning',
    }
    return colors[status as keyof typeof colors] || 'default'
  }

  const handleToggleStatus = (_id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    showNotification(`Flow ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success')
  }

  const handleEdit = (_id: string) => {
    showNotification('Opening flow editor...', 'info')
  }

  const handleDelete = (_id: string) => {
    showNotification('Flow deleted successfully', 'success')
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, flowId: string) => {
    event.stopPropagation()
    setMenuAnchor({ anchorEl: event.currentTarget, flowId })
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Flow Name',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.description}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'trigger',
      headerName: 'Trigger',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={getTriggerLabel(params.value)}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'totalRuns',
      headerName: 'Total Runs',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'successRate',
      headerName: 'Success Rate',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.value}%
          </Typography>
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'grey.200',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${params.value}%`,
                height: '100%',
                bgcolor: params.value >= 70 ? 'success.main' : params.value >= 40 ? 'warning.main' : 'error.main',
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      field: 'lastRun',
      headerName: 'Last Run',
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2" color="text.secondary">
            {format(params.value, 'MMM dd, HH:mm')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Never
          </Typography>
        ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <Switch
          checked={params.row.status === 'active'}
          onChange={() => handleToggleStatus(params.id as string, params.row.status)}
          size="small"
          color="success"
          disabled={params.row.status === 'draft'}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.id as string)}
        />,
        <IconButton
          size="small"
          onClick={(event) => handleMenuClick(event, params.id as string)}
        >
          <MoreIcon fontSize="small" />
        </IconButton>,
      ],
    },
  ]

  const handleCreateFlowComplete = () => {
    setCreateFlowOpen(false)
    showNotification('Flow created successfully!', 'success')
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Automation Flows
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateFlowOpen(true)}
          size="large"
        >
          Create Flow
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flexGrow: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h6" color="success.main" fontWeight="bold">
              3 Active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Running flows
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flexGrow: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              524 Total
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Executions
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flexGrow: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h6" color="warning.main" fontWeight="bold">
              72%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg success rate
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Flows Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={flows}
              columns={columns}
              loading={loading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor?.anchorEl}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleEdit(menuAnchor!.flowId)
          handleMenuClose()
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Flow
        </MenuItem>
        <MenuItem onClick={() => {
          showNotification('Flow duplicated successfully', 'success')
          handleMenuClose()
        }}>
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => {
          handleDelete(menuAnchor!.flowId)
          handleMenuClose()
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Flow Dialog */}
      <CreateFlowDialog
        open={createFlowOpen}
        onClose={() => setCreateFlowOpen(false)}
        onComplete={handleCreateFlowComplete}
      />
    </Box>
  )
}

export default Flows
